import React, { useState, useEffect } from 'react';
import apiService from '../../../services/api';

const OTP_RESEND_SECONDS = 60;

function asErrorText(value, fallback = 'Something went wrong. Please try again.') {
  if (typeof value === 'string') return value || fallback;
  if (value && typeof value === 'object') {
    const candidate = value.message || value.sqlMessage || value.error || value.detail;
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return fallback;
}

const NBFCCalculatorModule = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    amount: '',
    days: '',
    mobile: ''
  });
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendSeconds, setOtpResendSeconds] = useState(0);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'mobile') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
      setOtpSent(false);
      setOtp('');
      setOtpResendSeconds(0);
    } else if (name === 'amount') {
      processedValue = value.replace(/[^\d.]/g, '');
      const parts = processedValue.split('.');
      if (parts.length > 2) {
        processedValue = parts[0] + '.' + parts.slice(1).join('');
      }
      if (parts[1] && parts[1].length > 2) {
        processedValue = parts[0] + '.' + parts[1].slice(0, 2);
      }
    } else if (name === 'days') {
      processedValue = value.replace(/\D/g, '');
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const calculateInterestLocal = (amount, days) => {
    const interestRate = 14;
    const rateDecimal = interestRate / 100;
    const interestAmount = (amount * rateDecimal * days) / 365;
    const totalAmount = amount + interestAmount;
    const interest = Math.round(interestAmount * 100) / 100;
    const total = Math.round(totalAmount * 100) / 100;
    return {
      success: true,
      interest,
      total_amount: total,
      principal: amount,
      days,
      interest_rate: interestRate,
      currency: 'INR'
    };
  };

  /** Step 1 → 2: only validate (no totals until OTP verified) */
  const goToMobileStep = () => {
    setError('');
    if (!formData.amount || !formData.days) {
      setError('Please fill amount and credit days.');
      return;
    }
    const amount = parseFloat(formData.amount);
    const days = parseInt(formData.days, 10);
    if (isNaN(amount) || amount <= 0) {
      setError('Enter a valid purchase amount.');
      return;
    }
    if (isNaN(days) || days <= 0) {
      setError('Enter valid credit days.');
      return;
    }
    setOtpSent(false);
    setOtp('');
    setStep(2);
  };

  const sendWhatsappOtp = async () => {
    const m = formData.mobile.replace(/\D/g, '').slice(0, 10);
    if (m.length !== 10) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await apiService.whatsappSendOtp({ mobile: m });
      if (!res.success) {
        setError(asErrorText(res.message, 'Could not send OTP.'));
        return;
      }
      setOtpSent(true);
      setOtp('');
      setOtpResendSeconds(OTP_RESEND_SECONDS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otpResendSeconds <= 0 || !otpSent) return;
    const t = setInterval(() => setOtpResendSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpResendSeconds, otpSent]);

  const handleResendOtp = () => {
    if (otpResendSeconds > 0) return;
    setOtpResendSeconds(OTP_RESEND_SECONDS);
    sendWhatsappOtp();
  };

  /** After OTP verify: calculate + save, then show totals */
  const verifyOtpAndShowResults = async () => {
    const m = formData.mobile.replace(/\D/g, '').slice(0, 10);
    const otpClean = otp.replace(/\D/g, '').trim();
    if (m.length !== 10) {
      setError('Invalid mobile.');
      return;
    }
    if (otpClean.length < 4) {
      setError('Enter the OTP from WhatsApp.');
      return;
    }
    const purchaseAmount = parseFloat(formData.amount) || 0;
    const creditDays = parseInt(formData.days, 10) || 0;

    setError('');
    setLoading(true);
    try {
      const verify = await apiService.whatsappVerifyOtp({ mobile: m, otp: otpClean });
      if (!verify.success) {
        setError(asErrorText(verify.message, 'Invalid OTP.'));
        return;
      }

      let data = await apiService.calculateNBFC(purchaseAmount, creditDays);
      if (!data || !data.success) {
        data = calculateInterestLocal(purchaseAmount, creditDays);
      }

      setResults(data);
      const interestRate = data.interest_rate != null ? data.interest_rate : 14;
      const approxInterest = data.interest != null ? parseFloat(data.interest) : 0;
      const totalPayable =
        data.total_amount != null ? parseFloat(data.total_amount) : purchaseAmount;

      try {
        await apiService.saveCreditInterest({
          purchase_amount: purchaseAmount,
          required_credit_days: creditDays,
          interest_rate: interestRate,
          mobile: m,
          amount: purchaseAmount,
          approx_interest: approxInterest,
          total_payable_amount: totalPayable
        });
      } catch (e) {
        console.error(e);
      }

      setStep(3);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFormData({ amount: '', days: '', mobile: '' });
    setOtp('');
    setOtpSent(false);
    setOtpResendSeconds(0);
    setResults(null);
    setError('');
  };

  return (
    <div className="module-card nbfc-module">
      <div className="module-header">
        <div className="module-icon">
          <i className="fas fa-calculator"></i>
        </div>
        <h3>Credit Interest Calculator</h3>
      </div>
      <div
        className="calculator-mini-form"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '0',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0
        }}
      >
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', flex: 1 }}>
            <div className="form-field-mini">
              <label>Purchase amount (₹)</label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter purchase amount"
                required
                inputMode="decimal"
              />
            </div>
            <div className="form-field-mini">
              <label>Required credit days</label>
              <input
                type="text"
                name="days"
                value={formData.days}
                onChange={handleChange}
                placeholder="Enter credit days"
                required
                inputMode="numeric"
              />
            </div>
            <div className="form-field-mini">
              <label>Interest Rate</label>
              <input
                type="text"
                value="14%"
                readOnly
                style={{ backgroundColor: '#f5f5f5', color: '#064E8D', fontWeight: 600, cursor: 'default' }}
              />
            </div>
            <p style={{ fontSize: 12, color: '#555', margin: 0 }}>
              Totals are shown only after WhatsApp OTP verification.
            </p>
            {error && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  background: '#f8d7da',
                  borderRadius: 6,
                  color: '#721c24',
                  fontSize: 14
                }}
              >
                {error}
              </div>
            )}
            <div className="nbfc-info">
              <div className="info-item">
                <i className="fas fa-percentage"></i>
                <span>Competitive Rates</span>
              </div>
              <div className="info-item">
                <i className="fas fa-clock"></i>
                <span>Quick Approval</span>
              </div>
              <div className="info-item">
                <i className="fas fa-shield-alt"></i>
                <span>Secure Process</span>
              </div>
            </div>
            <button
              className="btn btn-primary btn-calculate"
              type="button"
              onClick={goToMobileStep}
              disabled={!formData.amount || !formData.days}
              style={{ width: '100%', marginTop: 'auto' }}
            >
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="credit-step active" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', flex: 1 }}>
            <div className="form-field-mini">
              <label>Purchase amount (₹)</label>
              <input type="text" value={formData.amount} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Required credit days</label>
              <input type="text" value={formData.days} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Mobile (WhatsApp)</label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="10-digit number"
                maxLength="10"
                readOnly={otpSent}
                style={otpSent ? { backgroundColor: '#f5f5f5' } : undefined}
                inputMode="numeric"
              />
            </div>
            {otpSent && (
              <div className="form-field-mini">
                <label>OTP from WhatsApp</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter OTP"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </div>
            )}
            {error && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  background: '#f8d7da',
                  borderRadius: 6,
                  color: '#721c24',
                  fontSize: 14
                }}
              >
                {error}
              </div>
            )}
            {!otpSent ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                <button
                  className="btn btn-primary btn-calculate"
                  type="button"
                  onClick={sendWhatsappOtp}
                  disabled={loading || formData.mobile.length !== 10}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Sending…' : 'Send OTP on WhatsApp'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setStep(1)} style={{ fontSize: 13 }}>
                  Back
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 'auto', paddingTop: 4 }}>
                <button
                  className="btn btn-primary btn-calculate"
                  type="button"
                  onClick={verifyOtpAndShowResults}
                  disabled={loading || otp.replace(/\D/g, '').length < 4}
                  style={{ width: '100%', padding: '10px 16px' }}
                >
                  {loading ? 'Verifying…' : 'Verify OTP & show calculation'}
                </button>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={loading}
                    onClick={() => {
                      setOtpSent(false);
                      setOtp('');
                      setError('');
                      setOtpResendSeconds(0);
                    }}
                    style={{ flex: 1, minWidth: 100, padding: '6px 10px', fontSize: 13 }}
                  >
                    Change number
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={loading || otpResendSeconds > 0}
                    onClick={handleResendOtp}
                    style={{ flex: 1, minWidth: 100, padding: '8px 12px', fontSize: 13 }}
                  >
                    {otpResendSeconds > 0 ? `Resend OTP in ${otpResendSeconds}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 3 && results && (
          <div
            className="credit-step active"
            id="credit-step-3"
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', flex: 1, overflowY: 'auto' }}
          >
            <div className="form-field-mini">
              <label>Purchase amount (₹)</label>
              <input type="text" value={formData.amount} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Required credit days</label>
              <input type="text" value={formData.days} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Interest Rate</label>
              <input
                type="text"
                value="14%"
                readOnly
                style={{ backgroundColor: '#f5f5f5', color: '#064E8D', fontWeight: 600 }}
              />
            </div>
            <div className="form-field-mini">
              <label>Mobile Number</label>
              <input type="text" value={formData.mobile} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="calculator-mini-result">
              <span className="result-label">AMOUNT</span>
              <span className="result-value">₹{parseFloat(formData.amount || 0).toFixed(2)}</span>
            </div>
            <div className="calculator-mini-result">
              <span className="result-label">APPROX INTEREST</span>
              <span className="result-value">
                ₹{results.interest ? parseFloat(results.interest).toFixed(2) : '0.00'}
              </span>
            </div>
            <div className="calculator-mini-result" id="mini-nbfc-total-result">
              <span className="result-label">TOTAL PAYABLE AMOUNT</span>
              <span className="result-value" id="credit-total-result">
                ₹
                {results.total_amount
                  ? parseFloat(results.total_amount).toFixed(2)
                  : parseFloat(formData.amount || 0).toFixed(2)}
              </span>
            </div>
            <button className="btn btn-secondary" type="button" onClick={handleReset} style={{ marginTop: 'auto' }}>
              Calculate Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NBFCCalculatorModule;
